/**
 * @name ListaDependentes
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Lista de dependentes usando cards reutilizáveis
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material';
import React from 'react';
import DependenteCard from './DependenteCard';

interface ListaDependentesProps {
     /**
      * Array de dependentes da view vw_saldos_alunos
      */
     dependentes: Array<{
          id_aluno: number | null;
          aluno: string | null;
          saldo_disponivel: number | null;
          limite_diario?: number | null;
          limite_semanal?: number | null;
     }>;

     /**
      * Estado de loading
      */
     loading?: boolean;

     /**
      * Handler para distribuir saldo
      */
     onDistribuirSaldo?: (idAluno: number) => void;

     /**
      * Handler para transferir saldo
      */
     onTransferirSaldo?: (idAluno: number) => void;

     /**
      * Handler para configurar dependente
      */
     onConfigurar?: (idAluno: number) => void;

     /**
      * Handler para ver detalhes do dependente
      */
     onVerDetalhes?: (idAluno: number) => void;
}

/**
 * Lista responsiva de dependentes
 * Exibe cards com informações dos dependentes da view vw_saldos_alunos
 */
const ListaDependentes: React.FC<ListaDependentesProps> = ({ dependentes, loading = false, onDistribuirSaldo = () => {}, onTransferirSaldo = () => {}, onConfigurar = () => {}, onVerDetalhes }) => {
     // Loading state
     if (loading) {
          return (
               <Box
                    sx={{
                         display: 'flex',
                         justifyContent: 'center',
                         alignItems: 'center',
                         minHeight: 200,
                         flexDirection: 'column',
                         gap: 2,
                    }}
               >
                    <CircularProgress size={40} />
                    <Typography
                         variant='body2'
                         color='text.secondary'
                    >
                         Carregando dependentes...
                    </Typography>
               </Box>
          );
     }

     // Empty state
     if (!dependentes || dependentes.length === 0) {
          return (
               <Alert
                    severity='info'
                    sx={{
                         textAlign: 'center',
                         '& .MuiAlert-message': {
                              width: '100%',
                         },
                    }}
               >
                    <Typography
                         variant='h6'
                         gutterBottom
                    >
                         Nenhum dependente encontrado
                    </Typography>
                    <Typography variant='body2'>Você ainda não possui dependentes cadastrados no sistema. Entre em contato com a administração para cadastrar seus filhos.</Typography>
               </Alert>
          );
     }

     // Render grid of dependentes
     return (
          <Box>
               <Grid
                    container
                    spacing={{ xs: 2, sm: 3 }}
                    sx={{
                         alignItems: 'stretch',
                         pb: 2, // Padding bottom para espaçamento
                    }}
               >
                    {dependentes.map((dependente, index) => {
                         // Garantir que temos um ID válido para a key
                         const keyId = dependente.id_aluno ?? `temp-${index}`;

                         return (
                              <Grid
                                   item
                                   xs={12}
                                   sm={6}
                                   md={6}
                                   lg={4}
                                   xl={3}
                                   key={keyId}
                                   sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                   }}
                              >
                                   <DependenteCard
                                        dependente={dependente}
                                        onDistribuirSaldo={onDistribuirSaldo}
                                        onTransferirSaldo={onTransferirSaldo}
                                        onConfigurar={onConfigurar}
                                        onVerDetalhes={onVerDetalhes}
                                        loading={loading}
                                   />
                              </Grid>
                         );
                    })}
               </Grid>

               {/* Informações adicionais */}
               <Box sx={{ mt: { xs: 2, sm: 3 }, textAlign: 'center', pb: 2 }}>
                    <Typography
                         variant='caption'
                         color='text.secondary'
                    >
                         {dependentes.length} dependente{dependentes.length !== 1 ? 's' : ''} encontrado{dependentes.length !== 1 ? 's' : ''}
                    </Typography>
               </Box>
          </Box>
     );
};

export default ListaDependentes;
